import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined'
import BookmarkAddedRoundedIcon from '@mui/icons-material/BookmarkAddedRounded'
import BookmarkRemoveRoundedIcon from '@mui/icons-material/BookmarkRemoveRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import { Tooltip } from '@mui/material'
import { hideEmail } from 'utils/user'
import useHideDuplicates from 'hooks/useHideDuplicates'
import { loadAllFilesRoutine, getBpFilesCountsRoutine } from 'ducks/files/actions'
import HideDuplicatesControl from 'components/atoms/HideDuplicatesControl'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import ProvenanceChip from 'features/bpDetails/Components/files/ProvenanceChip'
import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'
import { formatDate } from 'utils/date'
import {
  getDuplicates,
  bulkDeleteDuplicates,
  markFilesForDeletion,
  unmarkFilesFromDeletion,
  getMarkedFilesExport,
  downloadMarkedFilesCsv
} from 'services/fileService'

const formatFileSize = bytes => {
  if (!bytes || bytes === 0) return '-'
  const units = ['B', 'KB', 'MB', 'GB']
  let unitIndex = 0
  let size = bytes
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${size.toFixed(unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`
}

const getUploaderName = uploader => {
  if (!uploader) return '-'
  if (uploader.name) return uploader.name
  if (uploader.firstName && uploader.lastName) return `${uploader.firstName} ${uploader.lastName}`
  return uploader.email ? hideEmail(uploader.email) : '-'
}

const spLocation = file => [file.sharepointCustomView, file.sharepointFolderPath].filter(Boolean).join(' / ')

const DuplicatesPanel = ({ bp, canEdit, onOpenInsight }) => {
  const dispatch = useDispatch()
  const [activeSubTab, setActiveSubTab] = useState(0)
  const [hideDuplicates, setHideDuplicatesShared] = useHideDuplicates(bp?.id)
  const filesCounts = useSelector(state => state.files.filesCounts)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [platformDuplicates, setPlatformDuplicates] = useState([])
  const [sharepointDuplicates, setSharepointDuplicates] = useState([])
  const [summary, setSummary] = useState(null)
  const [pagination, setPagination] = useState(null)
  const [platformPage, setPlatformPage] = useState(0)
  const [sharepointPage, setSharepointPage] = useState(0)
  const [selectedFiles, setSelectedFiles] = useState(new Set())
  const [deletingFiles, setDeletingFiles] = useState(false)
  const [markingFiles, setMarkingFiles] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState(new Set())

  const fetchType = useCallback(async (type, page = 1) => {
    if (!bp?.id) return
    if (page === 1) setLoading(true)
    else setLoadingMore(true)
    try {
      const response = await getDuplicates(bp.id, { type, page, perPage: 20 })
      const data = response.data?.data || response.data
      const groups = (type === 'platform' ? data.platformDuplicates : data.sharepointDuplicates) || []
      const setter = type === 'platform' ? setPlatformDuplicates : setSharepointDuplicates
      setter(prev => {
        if (page <= 1) return groups
        const seen = new Set(prev.map(g => g.displayName))
        return [...prev, ...groups.filter(g => !seen.has(g.displayName))]
      })
      if (data.summary) setSummary(data.summary)
      setPagination(prev => ({ ...(prev || {}), [type]: data.pagination?.[type] || null }))
      if (type === 'platform') setPlatformPage(page)
      else setSharepointPage(page)
      setExpandedGroups(prev => new Set([...prev, ...groups.map(g => g.displayName)]))
    } catch (error) {
      toast.error('Could not load duplicate files. Please try again.')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [bp?.id])

  const refresh = useCallback(() => fetchType(activeSubTab === 0 ? 'platform' : 'sharepoint', 1), [activeSubTab, fetchType])

  const loadMore = useCallback(() => {
    if (loadingMore) return
    const type = activeSubTab === 0 ? 'platform' : 'sharepoint'
    const page = activeSubTab === 0 ? platformPage : sharepointPage
    const activePagination = activeSubTab === 0 ? pagination?.platform : pagination?.sharepoint
    if (activePagination && page < activePagination.totalPages) fetchType(type, page + 1)
  }, [activeSubTab, platformPage, sharepointPage, pagination, loadingMore, fetchType])

  useEffect(() => {
    if (bp?.id) {
      setSharepointDuplicates([])
      setSharepointPage(0)
      fetchType('platform', 1)
    }
  }, [bp?.id, fetchType])

  useEffect(() => {
    if (bp?.id) dispatch(getBpFilesCountsRoutine({ id: bp.id }))
  }, [bp?.id, dispatch])

  const toggleHideDuplicates = () => {
    setHideDuplicatesShared(!hideDuplicates)
    if (bp?.id) {
      dispatch(loadAllFilesRoutine({ bpId: bp.id }))
      dispatch(getBpFilesCountsRoutine({ id: bp.id }))
    }
  }

  const handleSubTabChange = newValue => {
    setActiveSubTab(newValue)
    setSelectedFiles(new Set())
    const type = newValue === 0 ? 'platform' : 'sharepoint'
    const loadedPage = newValue === 0 ? platformPage : sharepointPage
    if (loadedPage === 0) fetchType(type, 1)
  }

  const handleFileSelect = fileId => {
    setSelectedFiles(prev => {
      const next = new Set(prev)
      next.has(fileId) ? next.delete(fileId) : next.add(fileId)
      return next
    })
  }

  const handleSelectAllInGroup = (files, isSelected) => {
    setSelectedFiles(prev => {
      const next = new Set(prev)
      files.forEach(f => (isSelected ? next.delete(f.id) : next.add(f.id)))
      return next
    })
  }

  const toggleGroupExpanded = groupName => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      next.has(groupName) ? next.delete(groupName) : next.add(groupName)
      return next
    })
  }

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return
    if (!window.confirm(`Delete ${selectedFiles.size} duplicate file${selectedFiles.size === 1 ? '' : 's'}? This can’t be undone here.`)) return
    setDeletingFiles(true)
    try {
      const response = await bulkDeleteDuplicates(bp.id, Array.from(selectedFiles))
      toast.success(response.data?.message || `Deleted ${response.data?.deletedCount ?? selectedFiles.size} files`)
      setSelectedFiles(new Set())
      await refresh()
    } catch (error) {
      toast.error('Could not delete the selected files.')
    } finally {
      setDeletingFiles(false)
    }
  }

  const handleDeleteOne = async fileId => {
    if (deletingFiles) return
    if (!window.confirm('Delete this duplicate copy? This can’t be undone here.')) return
    setDeletingFiles(true)
    try {
      await bulkDeleteDuplicates(bp.id, [fileId])
      toast.success('Duplicate deleted.')
      await refresh()
    } catch (error) {
      toast.error('Could not delete this file.')
    } finally {
      setDeletingFiles(false)
    }
  }

  const handleMarkForDeletion = async () => {
    if (selectedFiles.size === 0) return
    setMarkingFiles(true)
    try {
      const response = await markFilesForDeletion(bp.id, Array.from(selectedFiles))
      toast.success(response.data?.message || `Marked ${response.data?.markedCount ?? selectedFiles.size} files for deletion`)
      setSelectedFiles(new Set())
      await refresh()
    } catch (error) {
      toast.error('Could not mark the files for deletion.')
    } finally {
      setMarkingFiles(false)
    }
  }

  const handleMarkOne = async fileId => {
    if (markingFiles) return
    setMarkingFiles(true)
    try {
      await markFilesForDeletion(bp.id, [fileId])
      toast.success('Marked for deletion. See the SharePoint cleanse tab.')
      await refresh()
    } catch (error) {
      toast.error('Could not mark this file for deletion.')
    } finally {
      setMarkingFiles(false)
    }
  }

  const handleUnmarkFromDeletion = async () => {
    if (selectedFiles.size === 0) return
    setMarkingFiles(true)
    try {
      const response = await unmarkFilesFromDeletion(bp.id, Array.from(selectedFiles))
      toast.success(response.data?.message || `Unmarked ${response.data?.unmarkedCount ?? selectedFiles.size} files`)
      setSelectedFiles(new Set())
      await refresh()
    } catch (error) {
      toast.error('Could not unmark the files.')
    } finally {
      setMarkingFiles(false)
    }
  }

  const handleExportMarked = async () => {
    try {
      const response = await getMarkedFilesExport(bp.id, 'json')
      const data = response.data?.data || response.data
      if (!data.files || data.files.length === 0) {
        toast.info('No files marked for deletion.')
        return
      }
      const text = data.files
        .map(f => `${f.displayName}\n  URL: ${f.sharepointWebUrl || 'N/A'}\n  Path: ${spLocation(f) || 'N/A'}\n  Marked by: ${f.markedByName || 'Unknown'} on ${f.markedAt ? new Date(f.markedAt).toLocaleDateString() : 'N/A'}`)
        .join('\n\n')
      await navigator.clipboard.writeText(text)
      toast.success(`Copied ${data.files.length} marked files to the clipboard.`)
    } catch (error) {
      toast.error('Could not export the marked files.')
    }
  }

  const handleDownloadCsv = async () => {
    try {
      const response = await downloadMarkedFilesCsv(bp.id)
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `sharepoint-duplicates-marked-${bp.id}-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('CSV downloaded.')
    } catch (error) {
      toast.error('Could not download the CSV.')
    }
  }

  const isPlatformTab = activeSubTab === 0
  const currentDuplicates = isPlatformTab ? platformDuplicates : sharepointDuplicates
  const activePagination = isPlatformTab ? pagination?.platform : pagination?.sharepoint
  const totalForTab = (isPlatformTab ? summary?.platformDuplicateGroups : summary?.sharepointDuplicateGroups) ?? currentDuplicates.length
  const platformDup = summary?.platformTotalDuplicates || 0
  const sharepointDup = summary?.sharepointTotalDuplicates || 0
  const dupFilesBothSources = platformDup + sharepointDup
  const dupGroupsBothSources = (summary?.platformDuplicateGroups || 0) + (summary?.sharepointDuplicateGroups || 0)
  const allFilesTotal = filesCounts?.totalWithDuplicates || 0
  const extraCopiesHidden = filesCounts?.duplicates || 0
  const uniqueFilesShown = Math.max(0, allFilesTotal - extraCopiesHidden)
  const fmt = n => (n || 0).toLocaleString('en-GB')

  return (
    <Wrap>
      {summary && (
        <SummaryCard>
          <SummaryTitle>What these numbers mean</SummaryTitle>
          {allFilesTotal > 0 && (
            <SummaryLine>
              This building has <b>{fmt(allFilesTotal)}</b> files in total. <b>{fmt(extraCopiesHidden)}</b> of them are extra duplicate cop{extraCopiesHidden === 1 ? 'y' : 'ies'}, so your Documents area hides those and shows the <b>{fmt(uniqueFilesShown)}</b> unique file{uniqueFilesShown === 1 ? '' : 's'}. That’s what the “Hide duplicates” switch below does (it’s on by default).
            </SummaryLine>
          )}
          <SummaryLine>
            The list below groups every file that shares its <b>name and size</b> with another: <b>{fmt(dupFilesBothSources)}</b> file{dupFilesBothSources === 1 ? '' : 's'} in <b>{fmt(dupGroupsBothSources)}</b> set{dupGroupsBothSources === 1 ? '' : 's'} (one set = one repeated name). This counts every copy in a set, including the one you keep{allFilesTotal > 0 ? ', so it’s a bigger number than the copies hidden above' : ''}. Open a set, keep the best copy, and clear the rest.
          </SummaryLine>
          {platformDup > 0 && sharepointDup > 0 && (
            <SummaryLine>
              <b>{fmt(platformDup)}</b> of these are Building Passport files you can delete here; the other <b>{fmt(sharepointDup)}</b> are in SharePoint. Mark those here, then delete them in SharePoint at the source.
            </SummaryLine>
          )}
          {platformDup > 0 && sharepointDup === 0 && (
            <SummaryLine>
              These are all Building Passport files, so you can delete the extra copies right here.
            </SummaryLine>
          )}
          {platformDup === 0 && sharepointDup > 0 && (
            <SummaryLine>
              These are all in SharePoint. SharePoint files can’t be deleted here, so mark them, then delete them in SharePoint at the source.
            </SummaryLine>
          )}
        </SummaryCard>
      )}

      <Toolbar>
        <ToggleSlot>
          <HideDuplicatesControl
            checked={hideDuplicates}
            count={filesCounts?.duplicates || 0}
            hint='Hides the extra duplicate copies from your Automatic view. Custom views are unaffected.'
            onChange={toggleHideDuplicates}
          />
        </ToggleSlot>
      </Toolbar>

      <SubTabs role='tablist'>
        <SubTab role='tab' aria-selected={isPlatformTab} $active={isPlatformTab} onClick={() => handleSubTabChange(0)}>
          Building Passport{summary ? <Count $active={isPlatformTab}>{summary.platformDuplicateGroups}</Count> : null}
        </SubTab>
        <SubTab role='tab' aria-selected={!isPlatformTab} $active={!isPlatformTab} onClick={() => handleSubTabChange(1)}>
          <CloudSyncOutlinedIcon style={{ fontSize: 16 }} /> SharePoint{summary ? <Count $active={!isPlatformTab}>{summary.sharepointDuplicateGroups}</Count> : null}
        </SubTab>
      </SubTabs>

      {canEdit && selectedFiles.size > 0 && (
        <BulkBar>
          <BulkInfo>{selectedFiles.size} file{selectedFiles.size === 1 ? '' : 's'} selected</BulkInfo>
          <ClearBtn type='button' onClick={() => setSelectedFiles(new Set())} disabled={deletingFiles || markingFiles}>Clear</ClearBtn>
          {isPlatformTab ? (
            <BulkBtn $tone='del' type='button' onClick={handleDeleteSelected} disabled={deletingFiles}>
              <DeleteOutlineRoundedIcon style={{ fontSize: 16 }} /> {deletingFiles ? 'Deleting…' : 'Delete selected'}
            </BulkBtn>
          ) : (
            <>
              <BulkBtn $tone='mark' type='button' onClick={handleMarkForDeletion} disabled={markingFiles}>
                <BookmarkAddedRoundedIcon style={{ fontSize: 16 }} /> Mark for deletion
              </BulkBtn>
              <BulkBtn $tone='ghost' type='button' onClick={handleUnmarkFromDeletion} disabled={markingFiles}>
                <BookmarkRemoveRoundedIcon style={{ fontSize: 16 }} /> Unmark
              </BulkBtn>
            </>
          )}
        </BulkBar>
      )}

      {!isPlatformTab && (
        <ExportHint>
          <ExportText><CloudSyncOutlinedIcon style={{ fontSize: 16 }} /> SharePoint files can’t be deleted here. Marking one only adds it to the SharePoint cleanse list. Nothing is removed from SharePoint until you delete it there at the source.</ExportText>
          <ExportBtns>
            <RowBtn type='button' onClick={handleExportMarked}><ContentCopyRoundedIcon style={{ fontSize: 14 }} /> Copy list</RowBtn>
            <RowBtn type='button' onClick={handleDownloadCsv}><DownloadRoundedIcon style={{ fontSize: 14 }} /> Download CSV</RowBtn>
          </ExportBtns>
        </ExportHint>
      )}

      {loading ? (
        <Skeletons>{Array.from({ length: 6 }).map((_, i) => <SkelRow key={i} />)}</Skeletons>
      ) : currentDuplicates.length === 0 ? (
        <EmptyState>
          <EmptyIconWrap><TaskAltRoundedIcon style={{ fontSize: 30 }} /></EmptyIconWrap>
          <EmptyHeadline>No duplicates found</EmptyHeadline>
          <EmptySub>
            {isPlatformTab
              ? 'No duplicate Building Passport files were detected here.'
              : 'No duplicate SharePoint files were found for this building.'}
          </EmptySub>
        </EmptyState>
      ) : (
        <>
          <CountLine>Showing {currentDuplicates.length} of {totalForTab.toLocaleString('en-GB')} duplicate group{totalForTab === 1 ? '' : 's'}</CountLine>
          <LegendLine>
            <MatchChip $kind='ident' as='span'>Identical</MatchChip> same name &amp; size, safe to clear extras.
            <MatchChip $kind='diff' as='span'><WarningAmberRoundedIcon style={{ fontSize: 12 }} /> Different size</MatchChip> contents may differ, check first.
          </LegendLine>
          <GroupList>
            {currentDuplicates.map((group, gi) => {
              const isExpanded = expandedGroups.has(group.displayName)
              const allSelected = group.files.every(f => selectedFiles.has(f.id))
              const someSelected = group.files.some(f => selectedFiles.has(f.id))
              const identical = group.matchAnalysis?.likelyTrueDuplicates
              return (
                <Group key={group.displayName + gi} $open={isExpanded}>
                  <GroupHeader type='button' $open={isExpanded} aria-expanded={isExpanded} onClick={() => toggleGroupExpanded(group.displayName)}>
                    {canEdit && (
                      <CheckSlot onClick={e => e.stopPropagation()}>
                        <CheckboxInput
                          type='checkbox'
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected }}
                          onChange={() => handleSelectAllInGroup(group.files, allSelected)}
                        />
                      </CheckSlot>
                    )}
                    <Chevron $open={isExpanded}><KeyboardArrowRightRoundedIcon style={{ fontSize: 20 }} /></Chevron>
                    <GroupName>{group.displayName}</GroupName>
                    <GroupBadges>
                      <CountChip>{group.count} copies</CountChip>
                      <MatchChip
                        $kind={identical ? 'ident' : 'diff'}
                        title={identical ? 'Same name and size, safe to remove the extra copies.' : 'The contents may differ, so check before deleting.'}
                      >
                        {!identical && <WarningAmberRoundedIcon style={{ fontSize: 12 }} />}
                        {identical ? 'Identical' : 'Same name · different size'}
                      </MatchChip>
                    </GroupBadges>
                  </GroupHeader>

                  {isExpanded && (
                    <GroupBody>
                      <FHead>
                        <span />
                        <span>File</span>
                        <span>From</span>
                        <span>Size</span>
                        <span>Uploaded by</span>
                        <span>Date added</span>
                        <span />
                      </FHead>
                      {group.files.map((file, fi) => {
                        const isSelected = selectedFiles.has(file.id)
                        const isKept = fi === 0 && !file.markedForDeletion
                        return (
                          <FRow key={file.id} $selected={isSelected} $marked={file.markedForDeletion}>
                            {canEdit ? (
                              <CheckSlot onClick={e => e.stopPropagation()}>
                                <CheckboxInput type='checkbox' checked={isSelected} onChange={() => handleFileSelect(file.id)} />
                              </CheckSlot>
                            ) : <span />}
                            <FName>
                              <NameText type='button' title='View document insight' onClick={() => onOpenInsight?.({ id: file.id, displayName: file.displayName })}>{file.displayName}</NameText>
                              {spLocation(file) && (
                                <Tooltip
                                  title={spLocation(file)}
                                  placement='top'
                                  arrow
                                  enterTouchDelay={0}
                                  slotProps={{ tooltip: { sx: { maxWidth: 460, fontSize: 12, lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap' } } }}
                                >
                                  <FPath><FolderOutlinedIcon style={{ fontSize: 12 }} /> {spLocation(file)}</FPath>
                                </Tooltip>
                              )}
                            </FName>
                            <span><ProvenanceChip provenance={file.provenance} /></span>
                            <Num>{formatFileSize(file.size)}</Num>
                            <Muted title={getUploaderName(file.uploader)}>{getUploaderName(file.uploader)}</Muted>
                            <Num>{formatDate(file.createdAt)}</Num>
                            <ActionCell>
                              {file.markedForDeletion
                                ? <MarkedTag>Marked</MarkedTag>
                                : isKept
                                  ? <KeepTag title='The copy we suggest keeping'>Keep</KeepTag>
                                  : (canEdit
                                    ? (isPlatformTab
                                      ? <RowBtn $tone='bad' type='button' disabled={deletingFiles} onClick={() => handleDeleteOne(file.id)}><DeleteOutlineRoundedIcon style={{ fontSize: 14 }} /> Delete</RowBtn>
                                      : <RowBtn $tone='mark' type='button' disabled={markingFiles} onClick={() => handleMarkOne(file.id)} title='Mark this copy for deletion in the SharePoint cleanse list'><BookmarkAddedRoundedIcon style={{ fontSize: 14 }} /> Mark</RowBtn>)
                                    : <span />)}
                            </ActionCell>
                          </FRow>
                        )
                      })}
                    </GroupBody>
                  )}
                </Group>
              )
            })}
          </GroupList>
          <InfiniteScrollSentinel
            hasMore={!!activePagination && (isPlatformTab ? platformPage : sharepointPage) < (activePagination.totalPages ?? 1)}
            loading={loadingMore}
            onLoadMore={loadMore}
          />
        </>
      )}
    </Wrap>
  )
}

export default DuplicatesPanel

const Wrap = styled.div``

const Toolbar = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px;
`
const ToggleSlot = styled.div`margin-left: auto;`

const SummaryCard = styled.div`
  background: #f7f9fb; border: 1px solid #e6e9ef; border-radius: 12px;
  padding: 13px 16px; margin-bottom: 14px; display: flex; flex-direction: column; gap: 7px;
`
const SummaryTitle = styled.div`font-size: 12px; font-weight: 800; letter-spacing: 0.02em; text-transform: uppercase; color: #8a90a0;`
const SummaryLine = styled.p`
  margin: 0; font-size: 13px; line-height: 1.55; color: #4a5163;
  b { color: #1b2233; font-weight: 800; font-variant-numeric: tabular-nums; }
`

const SubTabs = styled.div`display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px;`
const SubTab = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 7px; border: none; background: transparent;
  padding: 10px 14px; font-size: 14px; font-weight: 700; cursor: pointer; margin-bottom: -1px;
  color: ${({ $active }) => ($active ? '#1b2233' : '#64748b')};
  border-bottom: 2px solid ${({ $active }) => ($active ? '#D24724' : 'transparent')};
  &:hover { color: #1b2233; }
`
const Count = styled.span`
  font-size: 12px; font-weight: 800; padding: 1px 8px; border-radius: 999px;
  color: ${({ $active }) => ($active ? '#fff' : '#5b6472')};
  background: ${({ $active }) => ($active ? '#D24724' : '#eceef2')};
`

const BulkBar = styled.div`
  position: sticky; top: 0; z-index: 3; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 10px 14px; margin-bottom: 12px; border-radius: 10px; background: #1b2233; color: #fff;
`
const BulkInfo = styled.span`font-size: 13.5px; font-weight: 700; margin-right: auto;`
const ClearBtn = styled.button`
  font: inherit; padding: 6px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer;
  color: #e5e7eb; background: transparent; border: 1px solid #3b4256;
  &:hover:not(:disabled) { background: #2a3145; }
  &:disabled { opacity: 0.5; cursor: wait; }
`
const BulkBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
  padding: 6px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 800; cursor: pointer;
  color: ${({ $tone }) => ($tone === 'ghost' ? '#e5e7eb' : '#fff')};
  background: ${({ $tone }) => ($tone === 'del' ? RAG_LEVELS.critical.fg : $tone === 'mark' ? '#D24724' : 'transparent')};
  border: 1px solid ${({ $tone }) => ($tone === 'del' ? RAG_LEVELS.critical.fg : $tone === 'mark' ? '#D24724' : '#3b4256')};
  transition: filter 140ms ease;
  &:hover:not(:disabled) { filter: brightness(1.08); }
  &:disabled { opacity: 0.5; cursor: wait; }
`

const ExportHint = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 10px 13px; margin-bottom: 12px; border-radius: 10px; background: #eaf1fe; border: 1px solid #cfe0fb;
`
const ExportText = styled.span`display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; color: #3b5ba5; font-weight: 600;`
const ExportBtns = styled.div`display: inline-flex; gap: 6px;`

const RowBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
  padding: 6px 11px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer;
  color: ${({ $tone }) => ($tone === 'bad' ? RAG_LEVELS.critical.fg : $tone === 'mark' ? '#c2410c' : '#4a4f5e')};
  background: ${({ $tone }) => ($tone === 'bad' ? RAG_LEVELS.critical.bg : $tone === 'mark' ? '#ffedd5' : '#f1f3f7')};
  border: 1px solid ${({ $tone }) => ($tone === 'bad' ? `${RAG_LEVELS.critical.fg}33` : $tone === 'mark' ? '#c2410c33' : '#e6e8ec')};
  transition: filter 140ms ease;
  &:hover:not(:disabled) { filter: brightness(0.97); }
  &:disabled { opacity: 0.5; cursor: wait; }
`

const CountLine = styled.div`font-size: 12.5px; color: #8a90a0; margin-bottom: 6px;`
const LegendLine = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 12px; color: #8a90a0; margin-bottom: 12px;
`

const GroupList = styled.div`display: flex; flex-direction: column; gap: 10px;`
const Group = styled.div`border: 1px solid #eef0f3; border-radius: 12px; overflow: hidden; background: #fff;`
const GroupHeader = styled.button`
  font: inherit; width: 100%; display: flex; align-items: center; gap: 12px; text-align: left;
  padding: 13px 14px; cursor: pointer; border: none;
  background: ${({ $open }) => ($open ? '#fafbfc' : '#fff')};
  border-bottom: 1px solid ${({ $open }) => ($open ? '#eef0f3' : 'transparent')};
  transition: background 120ms ease;
  &:hover { background: #fafbfc; }
`
const Chevron = styled.span`
  flex: 0 0 auto; display: inline-flex; color: #98a0af;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')}); transition: transform 160ms ease;
`
const GroupName = styled.span`font-size: 14.5px; font-weight: 700; color: #1b2233; word-break: break-word; min-width: 0;`
const GroupBadges = styled.span`display: inline-flex; align-items: center; gap: 7px; flex-wrap: wrap; margin-left: auto; flex: 0 0 auto;`
const CountChip = styled.span`
  display: inline-flex; align-items: center; font-size: 11px; font-weight: 800; padding: 2px 9px; border-radius: 999px;
  color: #5b6472; background: #f0f1f5; white-space: nowrap;
`
const MatchChip = styled.span`
  display: inline-flex; align-items: center; gap: 3px; font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 999px; white-space: nowrap; cursor: help;
  color: ${({ $kind }) => ($kind === 'ident' ? RAG_LEVELS.low.fg : RAG_LEVELS.medium.fg)};
  background: ${({ $kind }) => ($kind === 'ident' ? RAG_LEVELS.low.bg : RAG_LEVELS.medium.bg)};
`

const GroupBody = styled.div`padding: 4px 6px 8px;`
const gridCols = '26px minmax(0, 2.4fr) 104px 78px minmax(0, 1.1fr) 96px 110px'
const FHead = styled.div`
  display: grid; grid-template-columns: ${gridCols}; align-items: center; gap: 10px; padding: 8px 10px; border-bottom: 1px solid #eef0f3;
  span { font-size: 10.5px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #98a0af; }
`
const FRow = styled.div`
  display: grid; grid-template-columns: ${gridCols}; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 8px; font-size: 13px;
  background: ${({ $selected, $marked }) => ($marked ? RAG_LEVELS.critical.bg : $selected ? '#eef4ff' : 'transparent')};
  &:hover { background: ${({ $selected, $marked }) => ($marked ? RAG_LEVELS.critical.bg : $selected ? '#e4edff' : '#fafbfc')}; }
`
const FName = styled.div`min-width: 0; display: flex; flex-direction: column; gap: 2px;`
const NameText = styled.button`
  font: inherit; display: block; width: 100%; text-align: left; border: none; background: none; padding: 0; cursor: pointer;
  font-weight: 600; color: #33becc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  &:hover { text-decoration: underline; }
`
const FPath = styled.span`display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: #8a90a0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: help; border-bottom: 1px dotted #d7dbe3; width: fit-content; max-width: 100%;`
const Num = styled.span`color: #6b7280; font-variant-numeric: tabular-nums; white-space: nowrap;`
const Muted = styled.span`color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`
const ActionCell = styled.div`display: flex; justify-content: flex-end; align-items: center; gap: 8px;`
const KeepTag = styled.span`
  display: inline-flex; align-items: center; font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 999px; cursor: help;
  color: ${RAG_LEVELS.low.fg}; background: ${RAG_LEVELS.low.bg};
`
const MarkedTag = styled.span`
  display: inline-flex; align-items: center; font-size: 11px; font-weight: 800; padding: 2px 9px; border-radius: 999px; white-space: nowrap;
  color: ${RAG_LEVELS.critical.fg}; background: #fff;
`

const CheckSlot = styled.div`flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;`
const CheckboxInput = styled.input`width: 16px; height: 16px; cursor: pointer; accent-color: #33becc; margin: 0;`

const EmptyState = styled.div`display: flex; flex-direction: column; align-items: center; text-align: center; padding: 52px 16px;`
const EmptyIconWrap = styled.div`
  display: inline-flex; align-items: center; justify-content: center; width: 60px; height: 60px; border-radius: 16px; margin-bottom: 14px;
  color: ${RAG_LEVELS.low.fg}; background: ${RAG_LEVELS.low.bg};
`
const EmptyHeadline = styled.div`font-size: 16px; font-weight: 800; color: #1b2233; margin-bottom: 4px;`
const EmptySub = styled.div`font-size: 13.5px; color: #7a8194; max-width: 380px; line-height: 1.5;`

const Skeletons = styled.div`display: flex; flex-direction: column; gap: 10px; padding-top: 4px;`
const SkelRow = styled.div`
  height: 54px; border-radius: 12px; background: #eef0f3;
  @keyframes shimmer { 0% { opacity: 0.55; } 50% { opacity: 1; } 100% { opacity: 0.55; } }
  animation: shimmer 1.4s ease-in-out infinite;
`
